import { inject, Injectable } from '@angular/core';
import {
  _decodeURIComponent,
  AppResourceManagerService, ComponentType,
  getFragmentUrl,
  IPageMinJSON
} from "@wms/design-container";
import { getValidJSON } from "@wm/core";
import * as cheerio from "cheerio";
import { widgetIdGenerator } from "../../../../projects/components-base/src/widgets/framework/widget-id-generator";
import { getWidgetPropsByType } from "@wm/components-base";
import { register } from "../../../../projects/components-base/src/widgets/framework/widget-registry";
import { BaseComponent } from "@wms/components";
import { BaseVariableService } from "@wms/variables";
import { BindTreeUtilsService, BindVariableService} from "@wms/services";
import { transpile } from "@wm/transpiler";

class Widget {
  name: string;
  widgetProps: any;
  widgetType: string;
  widgetSubType: string;
  attrs: any;
  binddataset: any;
  multiselect: any;
  multiple: any;
  datafield: any;
  displayfield: any;

  constructor(name: string, widgetProps: any, widgetType: string, widgetSubType: string, attrs: any) {
    this.name = name;
    this.widgetProps = widgetProps;
    this.widgetType = widgetType;
    this.widgetSubType = widgetSubType;
    this.attrs = attrs;

    if(this.attrs['dataset']) {
      this.binddataset = this.attrs.dataset?.trim() || '';
    }

    if(this.attrs['multiselect']) {
      this.multiselect = this.attrs.multiselect === 'true';
    }

    if(this.attrs['multiple']) {
      this.multiple = this.attrs.multiple === 'true';
    }

    if(this.attrs['datafield']) {
      this.datafield = this.attrs.datafield;
    }

    if(this.attrs['displayfield']) {
      this.displayfield = this.attrs.displayfield;
    }
  }

  getAttr(attrName: any): string | undefined {
    return this.attrs[attrName];
  }
}

const pageCache = new Map<string, {widgets: {widget: Widget, widgetId: string, name: string}[], variableInfo: any, transpiledMarkup: any}>();
const getPageCache = (name: string) => {
  return pageCache.get(name);
}

@Injectable({
  providedIn: 'root'
})
export class BindWidgetsVariablesUtilsService  extends BaseComponent {
  pages: any = [];
  selectedPage: any = {};

  private unregisterSubscriptions: any[] = [];
  private projectId = this.projectService.getProjectId();
  private resourceManager = inject(AppResourceManagerService);
  private variableService = inject(BaseVariableService);
  private bindTreeService = inject(BindTreeUtilsService);
  private bindVariableService = inject(BindVariableService);

  constructor() {
    super();
  }

  init() {
    this.pages = [];
    this.selectedPage = {};
    this.getPagesList();
  }
  private getPagesList() {
    this.projectService.getPagesList().then((pages: any) => {
      this.pages = pages;
      const mainPage = this.pages.find((page:any) => page.name ==='Main');
      this.selectedPage = mainPage;
      this.onPageChange(true);
    });
  }

  onPageChange(setActiveTab = false) {
    this.bindTreeService.selectedPage = this.selectedPage.name;
    this.loadResourcesOfFragment(this.selectedPage.name, ComponentType[this.selectedPage.type])
      .then(({ widgets, variableInfo, transpiledMarkup, pageName, pageType})=> {
        this.unRegisterWidgets();
        this.registerWidgets(widgets, this.selectedPage.name);
        this.bindTreeService.selectedPageDom = transpiledMarkup;
        this.variableService.setPageVariables(pageName, variableInfo, pageType);
        this.bindVariableService.initVariables(false, setActiveTab);
      });
  }
  loadResourcesOfFragment(pageName: string, pageType: any): Promise<any> {
    let pageInfo = getPageCache(pageName);
    if(pageInfo && pageInfo.widgets && pageInfo.variableInfo && pageInfo.transpiledMarkup) {
      return Promise.resolve({ ...pageInfo, pageName, pageType});
    } else {
      const url: string = getFragmentUrl(pageName, pageType, {projectId: this.projectId}) || '';
      return this.getPageData(url, pageName, pageType);
    }
  }

  public getPageData(url: string, pageName: string, pageType: string): Promise<IPageMinJSON | Error> {
    const data = this.resourceManager.get(url, true)
      .then(({markup, variables}: IPageMinJSON) => {
        const decodedMarkup = _decodeURIComponent(markup),
          $ = cheerio.load(decodedMarkup),
          transpiledMarkup = cheerio.load(transpile(decodedMarkup)?.markup || ''),
          variableInfo = getValidJSON(_decodeURIComponent(variables));

          let widgets: {widget: Widget, widgetId: string, name: string}[] = [];
          $('*').each((i,element: any)=> {
             if(element.type === 'tag' && element.name.startsWith('wm')) {
              const widgetId = widgetIdGenerator.nextUid(),
              name = element.attribs.name,
              widgetProps = getWidgetPropsByType(element.name),
              widgetType = element.name,
              widgetSubType = element.name,
              attrs = element.attribs,
              widget = new Widget(name, widgetProps, widgetType, widgetSubType, attrs);
            widgets.push({widget, widgetId, name});
          }
        });
        pageCache.set(pageName, {widgets, variableInfo, transpiledMarkup });
        return Promise.resolve({ widgets, variableInfo, transpiledMarkup, pageName, pageType})
      }, (e: any) => {
        return Promise.reject();
      });
    return data
  }

  private unRegisterWidgets() {
    this.unregisterSubscriptions.forEach(fn => fn());
  }
  
  private registerWidgets(widgets: any[], pageName: string) {
    widgets.forEach(({widget, widgetId, name}) => {
      const unregister = register(widget, undefined, pageName, "wm-" + name, name)
      this.unregisterSubscriptions.push(unregister);
    });
  }

  clearAll() {
    this.pages = [];
    this.selectedPage = {};
    this.bindTreeService.selectedPage = '';
    this.bindTreeService.selectedPageDom = '';
    this.unRegisterWidgets();
    pageCache.clear();
  }
}
